
import React, { useRef } from "react";
import { useFrame } from "@react-three/fiber";
import { Float } from "@react-three/drei";
import * as THREE from "three";

const AIPyramid = ({ isTalking }: { isTalking: boolean }) => {
  const sphereRef = useRef<THREE.Group>(null);
  const coreRef = useRef<THREE.Mesh>(null);
  const particlesRef = useRef<THREE.Group>(null);
  
  useFrame((state) => {
    const time = state.clock.getElapsedTime();
    
    // Base rotation
    if (sphereRef.current) {
      sphereRef.current.rotation.y = time * 0.15;
    }
    
    // Core pulsing
    if (coreRef.current) {
      if (isTalking) {
        // Energetic pulsing when talking
        const pulseScale = 0.5 + Math.sin(time * 6) * 0.1;
        coreRef.current.scale.set(pulseScale, pulseScale, pulseScale);
        
        // Brighter when talking
        (coreRef.current.material as THREE.MeshStandardMaterial).emissiveIntensity = 
          2.0 + Math.sin(time * 8) * 0.5;
      } else {
        // Gentle pulsing when idle
        const idlePulse = 0.4 + Math.sin(time * 1.5) * 0.05;
        coreRef.current.scale.set(idlePulse, idlePulse, idlePulse);
        
        (coreRef.current.material as THREE.MeshStandardMaterial).emissiveIntensity = 
          1.2 + Math.sin(time * 2) * 0.2;
      }
    }
    
    // Orbiting particles
    if (particlesRef.current) {
      particlesRef.current.children.forEach((particle, i) => {
        const p = particle as THREE.Mesh;
        // Different speeds for different particles
        const speed = 0.5 + (i % 3) * 0.2;
        const radius = 1.2 + (i % 5) * 0.05;
        
        // Spherical distribution using spherical coordinates
        const phi = (i % 10) * Math.PI / 5; // vertical angle
        const theta = time * speed + i; // horizontal angle
        
        p.position.x = radius * Math.sin(phi) * Math.cos(theta);
        p.position.y = radius * Math.cos(phi);
        p.position.z = radius * Math.sin(phi) * Math.sin(theta);
        
        // Particles get more energetic when talking
        if (isTalking) {
          (p.material as THREE.MeshStandardMaterial).emissiveIntensity = 
            1.5 + Math.sin(time * 10 + i) * 0.5;
        }
      });
    }
  });

  return (
    <Float rotationIntensity={0.1} floatIntensity={0.4} speed={1.2}>
      <group ref={sphereRef}>
        {/* Outer sphere shell */}
        <mesh>
          <sphereGeometry args={[1.2, 32, 32]} />
          <meshPhysicalMaterial 
            color="#1a9e41"
            transparent={true}
            opacity={0.2}
            roughness={0.1}
            metalness={0.8}
            clearcoat={1.0}
            clearcoatRoughness={0.1}
            transmission={0.95}
            ior={1.5}
          />
        </mesh>
        
        {/* Inner energy core */}
        <mesh ref={coreRef}>
          <sphereGeometry args={[0.5, 24, 24]} />
          <meshStandardMaterial
            color="#00dd55"
            emissive="#00ff66"
            emissiveIntensity={1.2}
            roughness={0.3}
            metalness={0.8}
          />
        </mesh>
        
        {/* Energy particles */}
        <group ref={particlesRef}>
          {[...Array(20)].map((_, i) => (
            <mesh key={i} position={[0, 0, 0]}>
              <dodecahedronGeometry args={[0.04 + Math.random() * 0.03, 0]} />
              <meshStandardMaterial 
                color="#88ffaa" 
                emissive="#00ff88"
                emissiveIntensity={1.5}
              />
            </mesh>
          ))}
        </group>
        
        {/* Glowing equator */}
        <mesh rotation={[Math.PI / 2, 0, 0]}>
          <torusGeometry args={[1.2, 0.03, 16, 100]} />
          <meshStandardMaterial 
            color="#00cc44"
            emissive="#00aa44"
            emissiveIntensity={0.8}
            transparent={true}
            opacity={0.7}
          />
        </mesh>
        
        {/* Energy beam when talking */}
        {isTalking && (
          <mesh position={[0, 1.2, 0]} rotation={[Math.PI / 2, 0, 0]}>
            <cylinderGeometry args={[0.05, 0.2, 1, 16, 1, true]} />
            <meshStandardMaterial
              color="#00ff66"
              emissive="#00ff66"
              emissiveIntensity={2}
              transparent={true}
              opacity={0.7}
            />
          </mesh>
        )}
      </group>
    </Float>
  );
};

export default AIPyramid;
